import type { Office } from "@/data/directory/types"

export interface RankedOffice extends Office {
  rank: number
  score: number
  badges: string[]
}

/**
 * Ranks offices by a weighted composite score:
 * - Rating (40%): normalised to 0-1 from 0-5
 * - Reviews (25%): capped at 500, normalised
 * - Experience (20%): years since established, capped at 150
 * - Listings (15%): capped at 200, normalised
 */
export function rankOffices(offices: Office[]): RankedOffice[] {
  const currentYear = new Date().getFullYear()

  return offices
    .map((office) => {
      const ratingScore = (office.rating / 5) * 40
      const reviewScore = Math.min(office.reviewCount / 500, 1) * 25
      const experienceScore =
        Math.min((currentYear - office.established) / 150, 1) * 20
      const listingsScore = Math.min(office.propertyCount / 200, 1) * 15
      const score = ratingScore + reviewScore + experienceScore + listingsScore

      const badges: string[] = []
      if (office.rating >= 4.8) badges.push("Top Rated")
      if (office.reviewCount >= 300) badges.push("Most Reviewed")
      if (currentYear - office.established >= 50) badges.push("Established")
      if (office.propertyCount >= 100) badges.push("High Volume")

      return { ...office, score, badges, rank: 0 }
    })
    .sort((a, b) => b.score - a.score)
    .map((office, index) => ({ ...office, rank: index + 1 }))
}
